import { authService } from '@/stores/auth/auth.service';
import { useAuthStore } from '@/stores/auth/auth.slice';
import { uuidv4 } from '@/utils/uuid';
import { computed, ref, watch } from 'vue';

const ROLE_LABELS: Record<string, string> = {
  store_manager: 'Responsable',
};

const STORE_LOOKUP_TIMEOUT_MS = 3_000;

const store = ref({ designation: '', city: '' });
let loadedStoreId = '';

const toLabel = (code: string) =>
  ROLE_LABELS[code] ??
  code.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const initialsOf = (name: string) =>
  name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

// Identité du compte réellement connecté. Le commerce est un confort :
// s'il est injoignable, la ligne reste sans nom de commerce.
export const useOperator = () => {
  const authStore = useAuthStore();

  const loadStore = async (storeId: string) => {
    if (loadedStoreId === storeId) return;
    loadedStoreId = storeId;
    const abortCrt = new AbortController();
    const timeout = setTimeout(() => abortCrt.abort(), STORE_LOOKUP_TIMEOUT_MS);
    try {
      const { data } = await authService.getStore(storeId, {
        requestId: uuidv4(),
        signal: abortCrt.signal,
        jwttoken: authStore.tokens.accessToken,
      });
      store.value = {
        designation: data?.designation ?? '',
        city: data?.metadata?.city ?? '',
      };
    } catch {
      loadedStoreId = '';
    } finally {
      clearTimeout(timeout);
    }
  };

  watch(
    () => authStore.decodedJwt.accessToken?.restaurant_ref,
    (storeId) => {
      if (storeId) loadStore(storeId);
    },
    { immediate: true },
  );

  const name = computed(() => {
    const user = authStore.userStaff as {
      fullname?: string;
      firstname?: string;
      lastname?: string;
      email?: string;
    } | null;
    return (
      user?.fullname ||
      [user?.firstname, user?.lastname].filter(Boolean).join(' ') ||
      user?.email ||
      authStore.decodedJwt.accessToken?.user_email ||
      ''
    );
  });
  const initials = computed(() => initialsOf(name.value));
  const role = computed(() => {
    const code = authStore.userRoles[0]?.code;
    return code ? toLabel(code) : '';
  });
  const storeLine = computed(() =>
    [store.value.designation, store.value.city].filter(Boolean).join(' · '),
  );

  return {
    name,
    initials,
    role,
    store: computed(() => store.value.designation),
    city: computed(() => store.value.city),
    storeLine,
  };
};
